import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import { createMemoStyles, safeRest, deprecate } from '@wds/utils';
import { Fade } from '@wds/motion';
import { IconX } from '@wds/icons';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var SEMANTIC_COLORS = /* #__PURE__ */Object.freeze(['primary', 'success', 'info', 'warning', 'error', 'upgrade', 'secondary', 'muted']);
var SIZES = /* #__PURE__ */Object.freeze(['lg', 'md', 'sm']);
/* #__PURE__ */

Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var useStyles$1 = createMemoStyles(function (_ref) {
  var spacing = _ref.spacing,
      type = _ref.type;
  return {
    wrenchCloseButton: function wrenchCloseButton(_ref2) {
      var component = _ref2.component;
      var componentSpecificStyles = {
        badge: {
          position: 'relative',
          top: 'none',
          right: 'none',
          padding: 0,
          marginLeft: spacing[3]
        },
        banner: {
          position: 'absolute',
          top: spacing[2],
          right: spacing[2],
          padding: 0
        },
        default: {
          position: 'absolute',
          right: spacing[1],
          top: spacing[1],
          padding: spacing[2]
        }
      };
      return _objectSpread(_objectSpread({}, componentSpecificStyles[component || 'default']), {}, {
        fontSize: type.fontSize.body,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '0',
        textDecoration: 'none',
        background: 'transparent',
        border: 'none',
        '&:hover': {
          transition: 'none',
          textDecoration: 'none'
        },
        '&:active': {
          transition: 'none',
          outline: 'none'
        }
      });
    }
  };
});
var useStyles$2 = useStyles$1;
var _excluded$1 = ["children", "component", "label", "onClick", "type", "variant"];

var CloseButton = function CloseButton(_ref) {
  _ref.children;
  var component = _ref.component,
      label = _ref.label,
      onClick = _ref.onClick;
  _ref.type;

  var variant = _ref.variant,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var theme = useTheme();

  var _useStyles = useStyles$2({
    theme: theme,
    component: component
  }),
      wrenchCloseButton = _useStyles.wrenchCloseButton;

  return /*#__PURE__*/React.createElement("button", Object.assign({
    onClick: onClick,
    type: "button",
    className: wrenchCloseButton,
    "aria-label": label
  }, safeRest(rest)), /*#__PURE__*/React.createElement(IconX, {
    title: "close",
    color: variant
  }));
};

CloseButton.displayName = "CloseButton";
CloseButton.propTypes = {
  children: PropTypes.string,
  component: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.string,
  variant: PropTypes.oneOf(['dark', 'light'])
};
CloseButton.defaultProps = {
  children: 'CloseButton',
  component: undefined,
  type: undefined,
  variant: 'light'
};
var CloseButton$1 = CloseButton;
var useStyles = createMemoStyles(function (theme) {
  var type = theme.type,
      spacing = theme.spacing,
      palette = theme.palette,
      radius = theme.radius,
      shadow = theme.shadow;
  var badge = {
    height: {
      sm: 24,
      md: 26,
      lg: 32
    },
    padding: {
      sm: spacing[3],
      md: spacing[4],
      lg: spacing[4]
    },
    borderRadius: radius.medium,
    boxShadow: shadow.none
  };
  return {
    wrenchBadge: function wrenchBadge(_ref) {
      var color = _ref.color,
          size = _ref.size;
      return {
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        fontSize: type.fontSize.bodySm,
        fontWeight: type.fontWeight.medium,
        fontFamily: type.fontFamily.base,
        backgroundColor: palette[color].main,
        color: palette[color].contrast,
        padding: [0, badge.padding[size]],
        height: badge.height[size],
        lineHeight: "".concat(badge.height[size], "px"),
        borderRadius: badge.borderRadius,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        verticalAlign: 'middle',
        maxWidth: '100%'
      };
    }
  };
});
var _excluded = ["children", "closeLabel", "color", "dismissible", "onClose", "show", "size"];

var Badge = function Badge(_ref) {
  var children = _ref.children,
      closeLabel = _ref.closeLabel,
      color = _ref.color,
      dismissible = _ref.dismissible,
      onClose = _ref.onClose,
      show = _ref.show,
      size = _ref.size,
      rest = _objectWithoutProperties(_ref, _excluded);

  deprecate({
    size: size
  }, 'size', 'md', 'Badge');
  var theme = useTheme();

  var _useStyles = useStyles({
    color: color,
    theme: theme,
    size: size,
    dismissible: dismissible
  }),
      wrenchBadge = _useStyles.wrenchBadge;

  var closeButtonVariant = ['muted', 'upgrade', 'info'].includes(color) ? 'dark' : 'light';

  var handleClick = function handleClick() {
    if (onClose) {
      onClose();
    }
  };

  return /*#__PURE__*/React.createElement(Fade, {
    in: show
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", Object.assign({
    className: wrenchBadge
  }, safeRest(rest)), /*#__PURE__*/React.createElement("span", null, children), dismissible && /*#__PURE__*/React.createElement(CloseButton$1, {
    label: closeLabel,
    variant: closeButtonVariant,
    onClick: handleClick,
    component: "badge"
  }))));
};

Badge.displayName = "Badge";
Badge.propTypes = {
  children: PropTypes.node.isRequired,
  closeLabel: PropTypes.string,

  /**
   * Set the Badge background color.
   */
  color: PropTypes.oneOf(SEMANTIC_COLORS),

  /**
   * Set visibility of Badge Close Button.
   */
  dismissible: PropTypes.bool,

  /**
   * Send in a custom onClose function.
   */
  onClose: PropTypes.func,

  /**
   * Set visibility of the Badge.
   */
  show: PropTypes.bool,

  /**
   * Set the height and text size of the Badge.
   */
  size: PropTypes.oneOf(SIZES)
};
Badge.defaultProps = {
  closeLabel: 'Dismiss badge',
  color: 'primary',
  dismissible: false,
  onClose: undefined,
  show: true,
  size: 'sm'
};
export { Badge };